import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { isAdmin, isAdminOrManager, isAdminOrFinance } from '../utils/enums'
import { getAppMode } from '../utils/appMode'
import { getSelectedBranchId } from '../utils/branch'

var config = process.env;
// ENI-260: los 7 paneles de estadísticas ocupaban 7 líneas del menú y empujaban Caja,
// Movimientos y Facturas fuera de la vista. Se agrupan en un desplegable: colapsado es UNA línea,
// y no se pierde ninguna ruta ni deep link (que es lo que sí pasaría con una pantalla de pestañas).
//
// El orden es el de uso, no el de creación: Pulso primero porque se mira todos los días, y Salud
// del sistema último porque es para el que arregla, no para el dueño.
const PANELES_ESTADISTICAS = [
    // Los ids 41-44 reemplazan a 23/24/22/26, que ya estaban tomados por ítems sueltos de este
    // MISMO menú (ver el bloque de abajo): con el id repetido, entrar a Conteo de stock, Conteo
    // — control, Usados o Carritos resaltaba además el panel de estadísticas homónimo Y abría
    // el desplegable de Estadísticas, como si estuvieras en una sección donde no estabas.
    { id: 41, ruta: '/pulse', icono: 'speed', label: 'Pulso' },
    // Id 37: el 36 era el último. El 28 ya lo usa Comandas (`/cafeteriaOrders`), y
    // compartirlo hacía que el menú resaltara la pantalla equivocada en las DOS direcciones.
    { id: 37, ruta: '/dayAnalytics', icono: 'today', label: 'Resumen del día' },
    { id: 1, ruta: '/generalStatistics', icono: 'insert_chart_outlined', label: 'Ventas y rentabilidad' },
    { id: 43, ruta: '/customerAnalytics', icono: 'groups', label: 'Clientes' },
    { id: 42, ruta: '/inventoryAnalytics', icono: 'inventory_2', label: 'Catálogo e inventario' },
    { id: 25, ruta: '/cafeteriaAnalytics', icono: 'local_cafe', label: 'Cafetería' },
    { id: 44, ruta: '/salonAnalytics', icono: 'weekend', label: 'Salón y mesas' },
    // Id 52: el máximo en uso era 51 (Series del club).
    { id: 52, ruta: '/usedGamesAnalytics', icono: 'autorenew', label: 'Usados y gift cards' },
    { id: 27, ruta: '/healthAnalytics', icono: 'healing', label: 'Salud del sistema' },
];

// ⚠️ Los `itemSelected` son un espacio de ids COMPARTIDO entre todas las pantallas, y repetir
// uno hace que el menú resalte la pantalla equivocada en las dos direcciones. Ya pasó cuatro
// veces más de las dos que decía este comentario: 28 (Comandas vs Resumen del día), 25
// (Cafetería vs Integración ML) y los 22/23/24/26 que arriba pasaron a 41-44.
//
// El grep de abajo NO alcanza para elegir un id: da el máximo, pero el hueco que deja un id
// libre en el medio no existe — todo id ya usado está tomado aunque no sea el máximo. Tomá
// SIEMPRE máximo+1, y si dos ramas abiertas toman el mismo, git no marca conflicto.
//     git grep -ho 'itemSelected={[0-9]*}' -- src/pages | grep -o '[0-9]*' | sort -n | tail -1
// Al 2026-10-02 el máximo en uso es 53 (Gift cards); antes, 52 (panel de Juegos usados). El 50 es Cartelera TV, del menú de
// retail: `npm run lint:sidebar-ids` NO lo hubiera cazado (compara dentro de cada menú, no
// entre los dos), así que el máximo se lee con el grep de arriba, que mira las dos listas.
//
// Para chequear que no quedó ninguno repetido DENTRO de un mismo menú:
//     npm run lint:sidebar-ids

const ALERT_BADGE_CACHE_KEY = 'providerOrderAlertBadge';
const ALERT_BADGE_CACHE_TTL_MS = 15 * 1000;
// Reposición en estantería: el badge lleva el NÚMERO, no un punto. La diferencia entre "hay 2"
// y "hay 30" es la que decide si el encargado va ahora o después del mediodía; un punto obliga a
// entrar a la pantalla para saberlo. TTL corto porque el número cambia con cada venta del
// mostrador, y el que repone quiere verlo bajar mientras trabaja.
// La clave lleva la SUCURSAL: `sessionStorage` sobrevive al reload que hace el cambio de
// sucursal, así que con una clave única el badge de la sucursal A se mostraba hasta un minuto
// estando ya en la B ("7 para reponer" con la pantalla diciendo "no queda nada").
const SHELF_BADGE_CACHE_PREFIX = 'shelfRestockBadge:';
const SHELF_BADGE_CACHE_TTL_MS = 60 * 1000;
const SHELF_BADGE_EVENT = 'shelf-restock-changed';

function shelfBadgeCacheKey() {
    let branch = null;
    try {
        branch = getSelectedBranchId();
    } catch (e) {
        // localStorage bloqueado: se cae a la sucursal por defecto.
    }
    return `${SHELF_BADGE_CACHE_PREFIX}${branch || '1'}`;
}
// TTL más largo que el del badge: un feature flag cambia por decisión de un admin, no solo.
// 5 minutos alcanza para que prender/apagar se vea sin recargar sesión y evita pedir el
// catálogo de flags en cada navegación.
const SACCUM_SYNC_FLAG_CACHE_KEY = 'saccumSyncFeatureFlag';
const SACCUM_SYNC_FLAG_CACHE_TTL_MS = 5 * 60 * 1000;
// Precios vs competencia: interruptor (`feature_precios_competencia`) y contador de avisos sin
// ver. El contador cambia una vez por día (con la lectura) o cuando alguien abre un detalle, y
// en ese caso la pantalla avisa con el evento: un minuto de cache alcanza.
const COMPETITOR_PRICES_FLAG_CACHE_KEY = 'competitorPricesFeatureFlag';
const COMPETITOR_PRICES_BADGE_CACHE_KEY = 'competitorPricesBadge';
const COMPETITOR_PRICES_BADGE_CACHE_TTL_MS = 60 * 1000;
const COMPETITOR_PRICES_BADGE_EVENT = 'competitorPricesBadgeChanged';
// Mismo criterio de cacheo que el badge de pedidos: el sidebar se monta en TODAS las
// pantallas y no puede pedir esto en cada navegación. TTL corto para que el punto se apague
// al toque cuando el encargado termina el conteo.
const COUNT_BADGE_CACHE_KEY = 'stockCountAlertBadge';
const COUNT_BADGE_CACHE_TTL_MS = 15 * 1000;

class SidebarComponent extends Component {

    constructor(props) {
        super(props);
        // Abierto si ya estás en un panel de estadísticas: aterrizar en una pantalla con su
        // sección del menú cerrada deja sin pistas de dónde estás y cómo volver a las hermanas.
        const enEstadisticas = PANELES_ESTADISTICAS.some((x) => x.id === Number(props.itemSelected));
        this.state = { stockAlert: false, countAlert: false, shelfPending: 0, saccumSyncEnabled: false, competitorPricesEnabled: false, competitorPricesUnseen: 0, estadisticasAbierto: enEstadisticas };
        this._interval = null;
    }

    componentDidMount() {
        this.loadCachedAlert();
        this.checkAlert();
        this.loadCachedCountAlert();
        this.checkCountAlert();
        this.checkShelfPending();
        this.checkSaccumSyncFlag();
        this.checkCompetitorPricesFlag();
        this._interval = setInterval(() => {
            this.checkAlert();
            this.checkCountAlert();
            this.checkShelfPending();
            this.checkCompetitorPricesBadge();
        }, 3 * 60 * 1000);
        // La pantalla de reposición avisa al marcar. Sin esto el badge no bajaba hasta el
        // intervalo de 3 minutos: se marcaban 5 juegos, el encabezado decía 2 y el menú 7.
        window.addEventListener(SHELF_BADGE_EVENT, this.onShelfPendingChanged);
        window.addEventListener(COMPETITOR_PRICES_BADGE_EVENT, this.checkCompetitorPricesBadge);
    }

    // `feature_saccum_sync`. Se cachea en sessionStorage con el mismo criterio que el badge de
    // stock: el sidebar se monta en TODAS las pantallas y no puede pedir el catálogo de flags
    // en cada navegación. Ante cualquier error queda APAGADO: esconder un ítem nuevo es
    // preferible a ofrecer una pantalla que el backend va a rechazar.
    checkSaccumSyncFlag = () => {
        if (!(isAdminOrManager(this.props.role))) {
            return;
        }

        try {
            const cached = sessionStorage.getItem(SACCUM_SYNC_FLAG_CACHE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed && parsed.expiresAt > Date.now()) {
                    this.setState({ saccumSyncEnabled: !!parsed.enabled });
                    return;
                }
                sessionStorage.removeItem(SACCUM_SYNC_FLAG_CACHE_KEY);
            }
        } catch (error) {
            sessionStorage.removeItem(SACCUM_SYNC_FLAG_CACHE_KEY);
        }

        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'configuration/settings/feature-flags', {
            headers: { 'access-token': localStorage.getItem('token') }
        })
        .then(res => res.json())
        .then(body => {
            if (body.error !== 0 || !body.data || !body.data.flags) {
                return;
            }
            const flag = body.data.flags.find(f => f.key === 'saccum_sync');
            const enabled = !!(flag && flag.value === true);
            sessionStorage.setItem(SACCUM_SYNC_FLAG_CACHE_KEY, JSON.stringify({
                enabled,
                expiresAt: Date.now() + SACCUM_SYNC_FLAG_CACHE_TTL_MS,
            }));
            this.setState({ saccumSyncEnabled: enabled });
        })
        .catch(() => {});
    }

    componentWillUnmount() {
        clearInterval(this._interval);
        window.removeEventListener(SHELF_BADGE_EVENT, this.onShelfPendingChanged);
        window.removeEventListener(COMPETITOR_PRICES_BADGE_EVENT, this.checkCompetitorPricesBadge);
    }

    // `feature_precios_competencia`: mismo trato que el de SACCUM. Apagado (o ante cualquier
    // error) el ítem no se ofrece y el contador ni se pide.
    checkCompetitorPricesFlag = () => {
        if (!(isAdminOrManager(this.props.role))) {
            return;
        }

        try {
            const cached = sessionStorage.getItem(COMPETITOR_PRICES_FLAG_CACHE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed && parsed.expiresAt > Date.now()) {
                    this.setState({ competitorPricesEnabled: !!parsed.enabled }, this.checkCompetitorPricesBadge);
                    return;
                }
                sessionStorage.removeItem(COMPETITOR_PRICES_FLAG_CACHE_KEY);
            }
        } catch (error) {
            sessionStorage.removeItem(COMPETITOR_PRICES_FLAG_CACHE_KEY);
        }

        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'configuration/settings/feature-flags', {
            headers: { 'access-token': localStorage.getItem('token') }
        })
        .then(res => res.json())
        .then(body => {
            if (body.error !== 0 || !body.data || !body.data.flags) {
                return;
            }
            const flag = body.data.flags.find(f => f.key === 'precios_competencia');
            const enabled = !!(flag && flag.value === true);
            sessionStorage.setItem(COMPETITOR_PRICES_FLAG_CACHE_KEY, JSON.stringify({
                enabled,
                expiresAt: Date.now() + SACCUM_SYNC_FLAG_CACHE_TTL_MS,
            }));
            this.setState({ competitorPricesEnabled: enabled }, this.checkCompetitorPricesBadge);
        })
        .catch(() => {});
    }

    // Avisos de precios de la competencia sin ver. Ante cualquier error queda como está: un
    // contador equivocado es peor que ninguno.
    checkCompetitorPricesBadge = () => {
        if (!(isAdminOrManager(this.props.role)) || !this.state.competitorPricesEnabled) {
            return;
        }

        try {
            const cached = sessionStorage.getItem(COMPETITOR_PRICES_BADGE_CACHE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed && parsed.expiresAt > Date.now()) {
                    this.setState({ competitorPricesUnseen: Number(parsed.unseen) || 0 });
                    return;
                }
                sessionStorage.removeItem(COMPETITOR_PRICES_BADGE_CACHE_KEY);
            }
        } catch (error) {
            sessionStorage.removeItem(COMPETITOR_PRICES_BADGE_CACHE_KEY);
        }

        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'competitor-prices/badge', {
            headers: { 'access-token': localStorage.getItem('token') }
        })
        .then(res => res.json())
        .then(body => {
            if (body.error !== 0 || !body.data) {
                return;
            }
            const unseen = Number(body.data.unseen) || 0;
            sessionStorage.setItem(COMPETITOR_PRICES_BADGE_CACHE_KEY, JSON.stringify({
                unseen,
                expiresAt: Date.now() + COMPETITOR_PRICES_BADGE_CACHE_TTL_MS,
            }));
            this.setState({ competitorPricesUnseen: unseen });
        })
        .catch(() => {});
    }

    onShelfPendingChanged = (evento) => {
        const products = evento && evento.detail ? evento.detail.products : null;
        if (products == null) {
            return;
        }
        this.setState({ shelfPending: Number(products) || 0 });
        try {
            sessionStorage.setItem(shelfBadgeCacheKey(), JSON.stringify({
                products: Number(products) || 0,
                expiresAt: Date.now() + SHELF_BADGE_CACHE_TTL_MS,
            }));
        } catch (e) {
            // sessionStorage bloqueado: el número en pantalla ya se actualizó igual.
        }
    }

    // Pendientes de reponer en estantería. Mismo criterio de cache que el badge de stock: el
    // sidebar se monta en TODAS las pantallas y no puede pegarle al backend en cada navegación.
    // Ante cualquier error queda en 0 — un badge equivocado es peor que ningún badge.
    checkShelfPending = () => {
        if (!(isAdminOrManager(this.props.role))) {
            return;
        }

        try {
            const cached = sessionStorage.getItem(shelfBadgeCacheKey());
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed && parsed.expiresAt > Date.now()) {
                    this.setState({ shelfPending: Number(parsed.products) || 0 });
                    return;
                }
                sessionStorage.removeItem(shelfBadgeCacheKey());
            }
        } catch (error) {
            sessionStorage.removeItem(shelfBadgeCacheKey());
        }

        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'shelf-restock/pending-count', {
            headers: { 'access-token': localStorage.getItem('token') }
        })
        .then(res => res.json())
        .then(body => {
            if (body.error !== 0 || !body.data) {
                return;
            }
            const products = Number(body.data.products) || 0;
            sessionStorage.setItem(shelfBadgeCacheKey(), JSON.stringify({
                products,
                expiresAt: Date.now() + SHELF_BADGE_CACHE_TTL_MS,
            }));
            this.setState({ shelfPending: products });
        })
        .catch(() => {});
    }

    loadCachedAlert = () => {
        try {
            const rawCache = sessionStorage.getItem(ALERT_BADGE_CACHE_KEY);
            if (!rawCache) {
                return;
            }

            const parsedCache = JSON.parse(rawCache);
            if (!parsedCache || parsedCache.expiresAt <= Date.now()) {
                sessionStorage.removeItem(ALERT_BADGE_CACHE_KEY);
                return;
            }

            this.setState({ stockAlert: !!parsedCache.triggered });
        } catch (error) {
            sessionStorage.removeItem(ALERT_BADGE_CACHE_KEY);
        }
    }

    saveCachedAlert = (triggered) => {
        sessionStorage.setItem(ALERT_BADGE_CACHE_KEY, JSON.stringify({
            triggered: !!triggered,
            expiresAt: Date.now() + ALERT_BADGE_CACHE_TTL_MS,
        }));
    }

    checkAlert = () => {
        if (!(isAdminOrManager(this.props.role))) {
            return;
        }

        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'provider-orders/alert-badge', {
            headers: { 'access-token': localStorage.getItem('token') }
        })
        .then(res => res.json())
        .then(data => {
            if (data.error === 0) {
                this.saveCachedAlert(data.data.triggered);
                this.setState({ stockAlert: data.data.triggered });
            }
        })
        .catch(() => {});
    }

    loadCachedCountAlert = () => {
        try {
            const rawCache = sessionStorage.getItem(COUNT_BADGE_CACHE_KEY);
            if (!rawCache) {
                return;
            }

            const parsedCache = JSON.parse(rawCache);
            if (!parsedCache || parsedCache.expiresAt <= Date.now()) {
                sessionStorage.removeItem(COUNT_BADGE_CACHE_KEY);
                return;
            }

            this.setState({ countAlert: !!parsedCache.triggered });
        } catch (error) {
            sessionStorage.removeItem(COUNT_BADGE_CACHE_KEY);
        }
    }

    // El punto rojo de "Conteo de stock": prendido mientras quede conteo del día por hacer.
    // Ante cualquier error queda APAGADO, como el de pedidos: un badge que aparece porque
    // se cayó la request enseña a ignorarlo.
    checkCountAlert = () => {
        if (!(isAdminOrManager(this.props.role))) {
            return;
        }

        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'stock/count-badge', {
            headers: { 'access-token': localStorage.getItem('token') }
        })
        .then(res => res.json())
        .then(data => {
            if (data.error === 0) {
                sessionStorage.setItem(COUNT_BADGE_CACHE_KEY, JSON.stringify({
                    triggered: !!data.data.triggered,
                    expiresAt: Date.now() + COUNT_BADGE_CACHE_TTL_MS,
                }));
                this.setState({ countAlert: !!data.data.triggered });
            }
        })
        .catch(() => {});
    }

    // Sidebar del "modo cafetería": solo las opciones de cafetería (Caja, POS,
    // Comandas, Menú, Ventas). El resto del retail no aparece.
    renderCafeteriaSidebar = () => {
        return (
            <aside className="main-sidebar col-12 col-md-3 col-lg-2 px-0" style={{ zIndex: 1000 }}>
                <div className="main-navbar" style={{ position: 'relative', zIndex: 2, boxShadow: '0 0.125rem 0.625rem rgba(90, 97, 105, 0.12)' }}>
                    <nav className="navbar align-items-stretch navbar-light bg-white flex-md-nowrap p-0" style={{ borderBottom: '1px solid #e1e5eb' }}>
                        <a className="navbar-brand w-100 mr-0" href="#" style={{ lineHeight: '25px' }}>
                            <div className="d-table m-auto">
                                <img id="main-logo" className="d-inline-block align-top mr-1" style={{ maxWidth: '100px' }} src="images/logo.png" alt="EnigmaGames" />
                            </div>
                        </a>
                    </nav>
                </div>
                <div className="nav-wrapper">
                    <ul className="nav flex-column">
                        <h6 className="main-sidebar__nav-title" style={{ background: '#f9f9f9' }}>Cafetería</h6>
                        {/* La Caja NO va acá: el cajón es uno solo y está en la tienda, así que
                            la apertura y el cierre se operan desde el modo retail. */}
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 27) ? 'active' : ''}`} to="/cafeteria"><i className="material-icons">point_of_sale</i><span>POS Cafetería</span></Link></li>
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 28) ? 'active' : ''}`} to="/cafeteriaOrders"><i className="material-icons">receipt_long</i><span>Comandas</span></Link></li>
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 31) ? 'active' : ''}`} to="/reservations"><i className="material-icons">event_seat</i><span>Mesas</span></Link></li>
                        {/* Mesas abiertas: publicación e inscripciones (Mesa Abierta). Va al lado
                            de Mesas porque las opera el mismo salón. Id 45: el 44 era el último
                            (namespace PLANO compartido por los dos menús — no reciclar ids). */}
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 45) ? 'active' : ''}`} to="/openTables"><i className="material-icons">people</i><span>Mesas abiertas</span></Link></li>
                        {/* Ludoteca: qué juegos se pueden jugar en las mesas. Va al lado de Mesas
                            y no en retail porque lo administra quien maneja el salón, y es lo que
                            alimenta el picker de juego de la reserva. Id 35: el 34 era el último. */}
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 35) ? 'active' : ''}`} to="/ludoteca"><i className="material-icons">casino</i><span>Ludoteca</span></Link></li>
                        {/* ENI-209: Eventos vive en cafetería, no en retail. El id es 33 y no
                            32: el 32 lo ocupa "Reposiciones" desde ENI-199. */}
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 33) ? 'active' : ''}`} to="/events"><i className="material-icons">event</i><span>Eventos</span></Link></li>
                        {/* 51: series del club (la jornada que se repite todas las semanas). Va
                            pegado a Eventos porque cada jornada ES un evento: la serie es la
                            plantilla y la pantalla desde donde se agrega la de la semana.
                            El id es 51 y no 50: el 50 lo ocupa Cartelera TV en el menú de
                            retail, y los ids son un namespace PLANO compartido por los dos
                            menús. `repeat` es un glifo viejo de Material Icons: los nombres
                            nuevos renderizan como la PALABRA con la fuente de este backoffice. */}
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 51) ? 'active' : ''}`} to="/series"><i className="material-icons">repeat</i><span>Series del club</span></Link></li>
                        {/* 36: torneos sueltos, los que no cuelgan de un evento con venta de entradas.
                            Los ids del sidebar son un namespace PLANO y compartido por los dos menús
                            (retail y cafetería): reciclar uno ilumina dos ítems a la vez (ENI-209). */}
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 36) ? 'active' : ''}`} to="/tournaments"><i className="material-icons">emoji_events</i><span>Torneos</span></Link></li>
                        {/* 49: socios del club (alta/renovación en el mostrador). Al lado de Eventos y
                            Torneos porque la jornada del club es un evento privado y la entrada de
                            socio se valida contra esto. 48 era el último id en uso (namespace plano). */}
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 49) ? 'active' : ''}`} to="/memberships"><i className="material-icons">card_membership</i><span>Membresías</span></Link></li>
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 26) ? 'active' : ''}`} to="/cafeteriaMenu"><i className="material-icons">restaurant_menu</i><span>Menú</span></Link></li>
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 29) ? 'active' : ''}`} to="/cafeteriaInsumos"><i className="material-icons">kitchen</i><span>Insumos</span></Link></li>
                        {/* ENI-199: el espejo de "Reposición de stock" de retail, para insumos. */}
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 32) ? 'active' : ''}`} to="/cafeteriaReposiciones"><i className="material-icons">local_shipping</i><span>Reposiciones</span></Link></li>
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 30) ? 'active' : ''}`} to="/cafeteriaImpresora"><i className="material-icons">print</i><span>Impresora</span></Link></li>
                        <li className="nav-item"><Link className={`nav-link ${(this.props.itemSelected == 8) ? 'active' : ''}`} to="/purchase"><i className="material-icons">shopping_cart</i><span>Ventas</span></Link></li>
                    </ul>
                </div>
            </aside>
        );
    }

	render() {
        // El padre se marca activo cuando cualquiera de sus hijos lo está: si no, estando en
        // Cafetería el menú no señala nada y parece que no estás en ninguna sección.
        const enEstadisticas = PANELES_ESTADISTICAS.some((x) => x.id === Number(this.props.itemSelected));
        const role = this.props.role;

        if (getAppMode() === 'cafeteria' && isAdminOrManager(role)) {
            return this.renderCafeteriaSidebar();
        }

        return (
            <aside className="main-sidebar col-12 col-md-3 col-lg-2 px-0" style={{zIndex: 1000}}>
                <div
                    className="main-navbar"
                    style={{
                        position: 'relative',
                        zIndex: 2,
                        boxShadow: '0 0.125rem 0.625rem rgba(90, 97, 105, 0.12)',
                    }}
                >
                    <nav
                        className="navbar align-items-stretch navbar-light bg-white flex-md-nowrap p-0"
                        style={{ borderBottom: '1px solid #e1e5eb' }}
                    >
                        <a className="navbar-brand w-100 mr-0" href="#" style={{lineHeight: '25px'}}>
                            <div className="d-table m-auto">
                                <img id="main-logo" className="d-inline-block align-top mr-1" style={{maxWidth: '100px'}} src="images/logo.png" alt="EnigmaGames" />
                            </div>
                        </a>
                    </nav>
                </div>

                <div className="nav-wrapper">
                    <ul className="nav flex-column">
                        <h6 className="main-sidebar__nav-title" style={{background: '#f9f9f9'}}>General</h6>
                        {isAdmin(role) ? (
                            <li className="nav-item">
                                {/* No es un <Link>: el padre no navega a ningún lado, sólo abre y
                                    cierra. Un link que no lleva a ninguna parte se clickea una vez
                                    y no se vuelve a intentar. */}
                                <a
                                    className={`nav-link ${enEstadisticas ? 'active' : ''}`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => this.setState({ estadisticasAbierto: !this.state.estadisticasAbierto })}
                                >
                                    <i class="material-icons">bar_chart</i>
                                    <span>Estadísticas</span>
                                    <i
                                        class="material-icons"
                                        style={{ float: 'right', fontSize: 18, marginRight: 0, opacity: 0.6 }}
                                    >{this.state.estadisticasAbierto ? 'expand_less' : 'expand_more'}</i>
                                </a>
                                {this.state.estadisticasAbierto && (
                                    <ul className="nav flex-column">
                                        {PANELES_ESTADISTICAS.map((panel) => (
                                            <li className="nav-item" key={panel.ruta}>
                                                <Link
                                                    className={`nav-link ${(this.props.itemSelected == panel.id) ? 'active' : ''}`}
                                                    to={panel.ruta}
                                                    style={{ paddingLeft: 34, fontSize: 13 }}
                                                >
                                                    <i class="material-icons" style={{ fontSize: 18 }}>{panel.icono}</i>
                                                    <span>{panel.label}</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ) : null}
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 12) ? 'active' : ''}`} to="/caja">
                                    <i class="material-icons">attach_money</i><span>Caja</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdmin(role) ? (<li className="nav-item" style={{display: (this.props.role == 1) ? 'block' : 'none'}}>
                            <Link className={`nav-link ${(this.props.itemSelected == 14) ? 'active' : ''}`} to="/movements">
                                <i class="material-icons">paid</i><span>Movimientos</span>
                            </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrFinance(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 17) ? 'active' : ''}`} to="/invoices">
                                    <i class="material-icons">receipt</i><span>Facturas</span>
                                </Link>
                            </li>) : null
                        }

                        {
                            isAdminOrManager(role) ? (
                                <h6 className="main-sidebar__nav-title" style={{background: '#f9f9f9'}}>Compras</h6>
                            ) : null
                        }

                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 8) ? 'active' : ''}`} to="/purchase">
                                    <i class="material-icons">shopping_cart</i><span>Ventas</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            /* Id 38: el 37 era el último (Resumen del día). Va pegado a Ventas
                               porque es la otra pantalla que se usa con la pistola en la mano. */
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 38) ? 'active' : ''}`} to="/priceCheck">
                                    <i class="material-icons">price_check</i><span>Consulta de precio</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            /* Id 54: el 53 era el último (Gift cards). Va con Ventas y Consulta de
                               precio: es lo que se arma con el cliente adelante, antes de vender. */
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 54) ? 'active' : ''}`} to="/presupuestos">
                                    <i className="material-icons">request_quote</i><span>Presupuestos</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item" style={{display: (this.props.role == 1) ? 'block' : 'none'}}>
                                <Link className={`nav-link ${(this.props.itemSelected == 6) ? 'active' : ''}`} to="/tickets">
                                    <i class="material-icons">article</i><span>Tickets</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 9) ? 'active' : ''}`} to="/shipping">
                                    <i class="material-icons">local_shipping</i><span>Envios</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 11) ? 'active' : ''}`} to="/discountCodes">
                                    <i class="material-icons">view_module</i><span>Códigos de descuento</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 53) ? 'active' : ''}`} to="/giftCards">
                                    <i className="material-icons">card_giftcard</i><span>Gift cards</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 16) ? 'active' : ''}`} to="/soldProducts">
                                    <i className="material-icons">history</i><span>Productos vendidos</span>
                                </Link>
                            </li>) : null
                        }
                        
                        {
                            isAdminOrManager(role) ? (
                                <h6 className="main-sidebar__nav-title" style={{background: '#f9f9f9'}}>Productos</h6>
                            ) : null
                        }
                        
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 4) ? 'active' : ''}`} to="/products">
                                    <i className="material-icons">view_module</i><span>Productos</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 22) ? 'active' : ''}`} to="/usedGames">
                                    <i className="material-icons">autorenew</i><span>Usados</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 13) ? 'active' : ''}`} to="/restocking">
                                    <i className="material-icons">add_circle_outline</i><span>Reposición de stock</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            /* ENI-32 — Va pegado a Reposición porque son las dos caras de lo mismo:
                               la reposición es la mercadería que entra, la factura es lo que se debe
                               por ella. Una reposición sin factura linkeada no genera deuda. */
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 36) ? 'active' : ''}`} to="/providerInvoices">
                                    <i className="material-icons">request_quote</i><span>Facturas a pagar</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            /* "Comparativa de precios": UNA entrada para las dos pantallas de precios
                               (contra la competencia y contra el proveedor), que por dentro se
                               eligen con pestañas. Ids 39 (proveedor; 36 y 38 ya estaban tomados) y
                               53 (competencia): cualquiera de los dos la marca activa. Se ofrece si
                               al menos uno de los dos interruptores está encendido, y lleva a la
                               que lo esté. El número son los avisos de la competencia sin ver. */
                            isAdminOrManager(role) && (this.state.saccumSyncEnabled || this.state.competitorPricesEnabled) ? (<li className="nav-item">
                                <Link
                                    className={`nav-link ${(this.props.itemSelected == 39 || this.props.itemSelected == 53) ? 'active' : ''}`}
                                    to={this.state.competitorPricesEnabled ? '/competitorPrices' : '/priceReview'}
                                    style={{ position: 'relative' }}
                                >
                                    <i className="material-icons">compare_arrows</i><span>Comparativa de precios</span>
                                    {this.state.competitorPricesEnabled && this.state.competitorPricesUnseen > 0 && (
                                        <span style={{
                                            position: 'absolute',
                                            top: '12px',
                                            right: '6px',
                                            minWidth: '20px',
                                            height: '18px',
                                            lineHeight: '18px',
                                            padding: '0 6px',
                                            borderRadius: '9px',
                                            background: '#c4183c',
                                            color: '#fff',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            textAlign: 'center',
                                            display: 'inline-block',
                                            flexShrink: 0
                                        }}>{this.state.competitorPricesUnseen}</span>
                                    )}
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 19) ? 'active' : ''}`} to="/stockMonitor" style={{ position: 'relative' }}>
                                    <i className="material-icons">show_chart</i>
                                    <span>Hacer pedido</span>
                                    {this.state.stockAlert && (
                                        <span style={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '10px',
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: '#c4183c',
                                            display: 'inline-block',
                                            flexShrink: 0
                                        }} />
                                    )}
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 20) ? 'active' : ''}`} to="/providerOrders">
                                    <i className="material-icons">assignment</i><span>Pedidos a proveedores</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 21) ? 'active' : ''}`} to="/stockTransfers">
                                    <i className="material-icons">swap_horiz</i><span>Transferencias</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 40) ? 'active' : ''}`} to="/mlIntegration">
                                    <i className="material-icons">sync_alt</i><span>Integración ML</span>
                                </Link>
                            </li>) : null
                        }

                        {
                            /* Id 46: el 45 era el último (Mesas abiertas). Nació como 41 y hubo que
                               moverlo — mientras la rama estaba abierta, Pulso tomó el 41 en main.
                               Los ids son un namespace PLANO compartido por los dos menús y
                               repetir uno ilumina la pantalla equivocada en las dos direcciones;
                               `npm run lint:sidebar-ids` lo verifica.
                               Va pegado a "Conteo de stock" porque son las dos rutinas que se
                               hacen CAMINANDO por el salón, y quien hace una hace la otra. */
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 46) ? 'active' : ''}`} to="/shelfRestock" style={{ position: 'relative' }}>
                                    {/* `library_books` y no `shelves`: la fuente Material Icons que
                                        empaqueta este backoffice es vieja y no tiene el glifo de
                                        `shelves` — con un nombre que no existe el navegador NO cae en un
                                        ícono por defecto, RENDERIZA LA PALABRA (medido: 168 px de ancho
                                        contra 24 de un ícono real, y el menú queda diciendo "shelves"). */}
                                    <i className="material-icons">library_books</i>
                                    {/* "Reponer estantería" y no "Reponer en estantería": el sidebar
                                        recorta con puntos suspensivos a partir de ~18 caracteres (el largo
                                        de "Productos vendidos", que sí entra) y el badge caía justo encima
                                        del recorte. El título de la pantalla sí lleva la frase completa. */}
                                    <span>Reponer estantería</span>
                                    {this.state.shelfPending > 0 && (
                                        <span style={{
                                            position: 'absolute',
                                            top: '12px',
                                            right: '6px',
                                            minWidth: '20px',
                                            height: '18px',
                                            lineHeight: '18px',
                                            padding: '0 6px',
                                            borderRadius: '9px',
                                            background: '#c4183c',
                                            color: '#fff',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            textAlign: 'center',
                                            display: 'inline-block',
                                            flexShrink: 0
                                        }}>{this.state.shelfPending}</span>
                                    )}
                                </Link>
                            </li>) : null
                        }

                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 23) ? 'active' : ''}`} to="/stockCount" style={{ position: 'relative' }}>
                                    <i className="material-icons">fact_check</i>
                                    <span>Conteo de stock</span>
                                    {this.state.countAlert && (
                                        <span style={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '10px',
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: '#c4183c',
                                            display: 'inline-block',
                                            flexShrink: 0
                                        }} />
                                    )}
                                </Link>
                            </li>) : null
                        }

                        {/* El control del conteo es admin-only: es donde se ve el stock del
                            sistema y se ajusta la diferencia. El encargado cuenta a ciegas
                            arriba ("Conteo de stock") y no debe ver este lado. */}
                        {
                            isAdmin(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 24) ? 'active' : ''}`} to="/stockCountAdmin">
                                    <i className="material-icons">checklist</i><span>Conteo — control</span>
                                </Link>
                            </li>) : null
                        }

                        {/* El histórico de stock de un juego: se llega desde una discrepancia
                            ("¿y qué pasó con el stock de este?") y también sola, buscando el
                            juego. Admin como el control: muestra stock del sistema y ventas. */}
                        {
                            isAdmin(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 48) ? 'active' : ''}`} to="/stockHistory">
                                    <i className="material-icons">history</i><span>Histórico de stock</span>
                                </Link>
                            </li>) : null
                        }

                        {
                            isAdminOrManager(role) ? (
                                <h6 className="main-sidebar__nav-title" style={{background: '#f9f9f9'}}>Usuarios</h6>
                            ) : null
                        }

                        {
                            isAdmin(role) ? (<li className="nav-item" style={{display: (this.props.role == 1) ? 'block' : 'none'}}>
                                <Link className={`nav-link ${(this.props.itemSelected == 7) ? 'active' : ''}`} to="/admin">
                                    <i className="material-icons"></i><span>Administradores</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 3) ? 'active' : ''}`} to="/users">
                                    <i className="material-icons">people</i><span>Clientes</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 26) ? 'active' : ''}`} to="/carts">
                                    <i className="material-icons">shopping_cart</i><span>Carritos</span>
                                </Link>
                            </li>) : null
                        }
                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 10) ? 'active' : ''}`} to="/providers">
                                    <i className="material-icons">people</i><span>Proveedores</span>
                                </Link>
                            </li>) : null
                        }
                        
                        {
                            isAdminOrManager(role) ? (
                                <h6 className="main-sidebar__nav-title" style={{background: '#f9f9f9'}}>Extras</h6>
                            ) : null
                        }

                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 15) ? 'active' : ''}`} to="/labels">
                                    <i className="material-icons">add_circle_outline</i><span>Etiquetas</span>
                                </Link>
                            </li>) : null
                        }

                        {
                            isAdmin(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 18) ? 'active' : ''}`} to="/emailTemplates">
                                    <i className="material-icons">email</i><span>Plantillas de email</span>
                                </Link>
                            </li>) : null
                        }

                        {
                            /* `receipt_long` y no un icono más literal de correo: la fuente de
                               Material Icons del backoffice es vieja y los nombres nuevos
                               (outbox, forward_to_inbox) renderizan como la PALABRA. Éste ya se
                               usa en el sidebar, o sea que existe seguro. */
                            isAdmin(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 47) ? 'active' : ''}`} to="/emailLog">
                                    <i className="material-icons">receipt_long</i><span>Mails enviados</span>
                                </Link>
                            </li>) : null
                        }

                        {
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 34) ? 'active' : ''}`} to="/portadaImages">
                                    <i className="material-icons">image</i><span>Imágenes de portada</span>
                                </Link>
                            </li>) : null
                        }

                        {
                            /* Id 50: el 49 era el último tomado (contando las ramas abiertas).
                               Los ids son un namespace PLANO compartido por los dos menús y
                               repetir uno ilumina la pantalla equivocada en las dos direcciones;
                               `npm run lint:sidebar-ids` lo verifica.
                               Va pegado a "Imágenes de portada" porque son las dos pantallas de
                               "qué se ve", una para el sitio y otra para el local.
                               `tv` es un glifo viejo de Material Icons y existe seguro: los
                               nombres nuevos renderizan como la PALABRA con la fuente que
                               empaqueta este backoffice. */
                            isAdminOrManager(role) ? (<li className="nav-item">
                                <Link className={`nav-link ${(this.props.itemSelected == 50) ? 'active' : ''}`} to="/cartelera">
                                    <i className="material-icons">tv</i><span>Cartelera TV</span>
                                </Link>
                            </li>) : null
                        }

                        {/* ENI-209: Eventos se movió al sidebar de Cafetería. El título de
                            sección se va junto con el ítem: si no, queda un encabezado
                            huérfano sin nada debajo. */}
                    </ul>
                </div>
            </aside>
        );
    }
}

export default SidebarComponent;
